import { useEffect, useRef } from "react";
import { Plus, X } from "lucide-react";
import Input from "../../../../components/Input/Input";
import Button from "../../../../components/Button/Button";

/**
 * Numbered list of text inputs, used for learning objectives and bullet-list sections.
 * Enter adds a new item below (instead of submitting the lesson plan form).
 */
const NumberedListInput = ({ items, onChange, addLabel = "Add", placeholder = "Enter your content here", name = "item" }) => {
  const inputRefs = useRef([]);
  const focusIndexRef = useRef(null);
  const idPrefixRef = useRef(`lp-list-${Math.random().toString(36).slice(2, 8)}`);

  // Focus the row that was just inserted
  useEffect(() => {
    if (focusIndexRef.current === null) return;
    inputRefs.current[focusIndexRef.current]?.focus();
    focusIndexRef.current = null;
  }, [items.length]);

  const updateItem = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));
  const removeItem = (index) => onChange(items.length > 1 ? items.filter((_, i) => i !== index) : [""]);

  const insertItemAfter = (index) => {
    focusIndexRef.current = index + 1;
    onChange([...items.slice(0, index + 1), "", ...items.slice(index + 1)]);
  };

  return (
    <div className="lp-numbered-list">
      {items.map((item, index) => (
        <div key={index} className="lp-numbered-list__row">
          <span className="lp-numbered-list__index">{index + 1}</span>
          <Input
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            name={`${name}_${index}`}
            id={`${idPrefixRef.current}-${index}`}
            value={item}
            placeholder={placeholder}
            onChangeHandler={(event) => updateItem(index, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                insertItemAfter(index);
              }
            }}
            aria-label={`Item ${index + 1}`}
          />
          <Button
            variant="link"
            size="sm"
            className="lp-btn-icon-only"
            icon={<X size={16} />}
            onClick={() => removeItem(index)}
            aria-label="Remove item"
            title="Remove"
          />
        </div>
      ))}
      <Button
        variant="link"
        size="sm"
        className="lp-btn-add"
        icon={<Plus size={14} />}
        onClick={() => insertItemAfter(items.length - 1)}
      >
        {addLabel}
      </Button>
    </div>
  );
};

export default NumberedListInput;
